import React, { useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import Section from './Section';

const EXPERIENCES = [
  {
    role: "Software Engineer Intern",
    company: "Rocket Mortgage - Payments & Digital Transactions",
    location: "Detroit, MI",
    date: "May 2026 – Aug 2026",
    suit: "♥",
    rank: "1",
    bullets: [
      "Spearheaded an agentic end-to-end testing pipeline for frontend features serving 10M+ users, developing a mock backend framework bridging the TypeScript frontend and GraphQL microservices across 50+ payment services.",
      "Refactored and extended GraphQL payment fields across 20+ backend services and redesigned React/TypeScript frontend payment pages, shipping production functionality impacting 10M+ users.",
      "Built Dynatrace and Amplitude dashboards analyzing traffic across payment flows, identifying user drop-off points and driving TypeScript/React frontend changes that increased successful mortgage payments and throughput by 5%.",
    ],
  },
  {
    role: "Software Engineer (Student Staff)",
    company: "University of Michigan - Housing Information Services",
    location: "Ann Arbor, MI",
    date: "Sept 2025 – Present",
    suit: "♠",
    rank: "2",
    bullets: [
      "Spearheaded development of agentic email workflow platform handling 1,000+ student emails, reducing responses from 5 steps to 2, cutting turnaround time by 30%, and standardizing replies for 80% of common inquiry types.",
      "Achieved 96% housing policy response accuracy by building a LangChain RAG pipeline with FAISS vector indexing and HuggingFace embeddings over official policy documents, enabling context-aware semantic retrieval.",
      "Increased first-response resolution by 20% by building FastAPI backend services that classify inquiry intent using the Anthropic API and develop responses with real-time StarRez room and contract data (REST + GraphQL).",
    ],
  },
  {
    role: "Web Developer",
    company: "The Michigan Daily Newspaper",
    location: "Ann Arbor, MI",
    date: "Aug 2025 – Present",
    suit: "♦",
    rank: "3",
    bullets: [
      "Enabled non-technical staff to independently create Ann Arbor Geoguessr games by building an authenticated backend constructor, cutting developer involvement and enabling faster content production at scale.",
      "Accelerated end-to-end game publishing by engineering a REST API with SvelteKit and TypeScript, delivering authenticated CRUD endpoints and scheduled publishing to support rapid iteration and reliable releases.",
      "Implemented database architecture using SQLite/Cloudflare D1 with JSON serialization, migrations, triggers, and referential integrity, ensuring reliable game data persistence and long-term maintainability for the web developer.",
    ],
  },
  {
    role: "Founding Software Engineer",
    company: "ShipDB",
    location: "Ann Arbor, MI",
    date: "Aug 2025 – Present",
    suit: "♣",
    rank: "4",
    bullets: [
      "Developed an agentic AI platform using Python, FastAPI, Claude, and React/TypeScript that converts natural-language product descriptions into production-ready cloud databases, reducing setup time to under 5 minutes.",
      "Built an end-to-end AWS-integrated database deployment pipeline supporting DynamoDB and PostgreSQL (RDS), automating schema creation, provisioning, and credential validation via boto3 and Supabase APIs.",
      "Generated 30+ schemas with 85% first-pass deployment success using a TypeScript + React chat interface and FastAPI backend, reducing schema iteration cycles to under 2 revisions per project for one-click deployment.",
    ],
  },
  {
    role: "Programming Language Researcher",
    company: "University of Michigan - Future of Programming Lab (F.O.P)",
    location: "Ann Arbor, MI",
    date: "May 2025 – Sept 2025",
    suit: "♦",
    rank: "5",
    bullets: [
      "Conducting research on the Hazel Structure Editor under Dr. Cyrus Omar, utilizing OCaml to design interactive programming environments focused on live feedback and semantic analysis.",
      "Completed onboarding by studying EECS 490 lectures and assignments on functional programming, covering closures, environments, and evaluation in OCaml—later applied in building the ALF interpreter.",
    ],
  },
];

const ExperienceCard = ({ experience, index, isVisible, hasDealt, showHint, onClick }) => {
  const isRed = experience.suit === '♥' || experience.suit === '♦';
  const cardWidth = 160;
  const gap = 16;
  const startX = -((index - 0) * (cardWidth + gap));

  let cardClass = 'deal-card';
  if (isVisible) cardClass += hasDealt ? ' dealt' : ' deal-visible';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onClick(index); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(index); } }}
      className={cardClass}
      style={{
        '--deal-delay': `${index * 200}ms`,
        '--start-x': `${startX}px`,
        '--start-y': '0px',
        '--flip-duration': '200ms',
        '--slide-duration': '200ms',
      }}
    >
      <div className="card-inner">
        <div className="card-face card-front">
          <div className="poker-card">
            <div className="corner top-left">
              <span className="rank">{experience.rank}</span>
              <span className={`suit ${isRed ? 'red' : ''}`}>{experience.suit}</span>
            </div>
            <div className="corner bottom-right">
              <span className="rank">{experience.rank}</span>
              <span className={`suit ${isRed ? 'red' : ''}`}>{experience.suit}</span>
            </div>
            <div className={`center-content ${isRed ? 'red' : ''}`}>
              <div className="experience-title">{experience.role}</div>
              <div className="experience-company">{experience.company}</div>
              {experience.location && <div className="experience-location">{experience.location}</div>}
              <div className="experience-date">{experience.date}</div>
              {experience.bullets && (
                <ul className="experience-bullets">
                  {experience.bullets.map((b, i) => <li key={i} className="experience-desc">{b}</li>)}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      {showHint && (
        <div className="exp-click-hint">
          <svg className="exp-hint-arrow" viewBox="0 0 40 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 48 C20 30, 20 20, 20 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M12 12 L20 0 L28 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
          <span className="exp-hint-text">click me!</span>
        </div>
      )}
    </div>
  );
};

const ExpandedOverlay = ({ experience, onClose }) => {
  const isRed = experience.suit === '♥' || experience.suit === '♦';

  return (
    <div className="exp-overlay" onClick={onClose} role="presentation">
      <div className="exp-overlay-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="experience-card-close" onClick={onClose} aria-label="Close">×</button>
        <div className="poker-card">
          <div className="corner top-left">
            <span className="rank">{experience.rank}</span>
            <span className={`suit ${isRed ? 'red' : ''}`}>{experience.suit}</span>
          </div>
          <div className="corner bottom-right">
            <span className="rank">{experience.rank}</span>
            <span className={`suit ${isRed ? 'red' : ''}`}>{experience.suit}</span>
          </div>
          <div className={`center-content ${isRed ? 'red' : ''}`}>
            <div className="experience-title">{experience.role}</div>
            <div className="experience-company">{experience.company}</div>
            {experience.location && <div className="experience-location">{experience.location}</div>}
            <div className="experience-date">{experience.date}</div>
            {experience.bullets && (
              <ul className="experience-bullets">
                {experience.bullets.map((b, i) => <li key={i} className="experience-desc">{b}</li>)}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ExperienceSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasDealt, setHasDealt] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible && !hasDealt) {
      const t = setTimeout(() => setHasDealt(true), EXPERIENCES.length * 200 * 2 + 1300);
      return () => clearTimeout(t);
    }
  }, [isVisible, hasDealt]);

  useEffect(() => {
    if (expandedIndex === null) return;
    const onKey = (e) => { if (e.key === 'Escape') setExpandedIndex(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expandedIndex]);

  return (
    <Section id="experience" className="section-dark" ref={sectionRef}>
      <Container>
        <h2 className="section-title text-center">Experience</h2>
        <div className="runout experience-runout">
          {EXPERIENCES.map((exp, idx) => (
            <ExperienceCard
              key={idx}
              experience={exp}
              index={idx}
              isVisible={isVisible}
              hasDealt={hasDealt}
              showHint={idx === 0 && hasDealt && expandedIndex === null}
              onClick={(i) => setExpandedIndex(i)}
            />
          ))}
        </div>
        {expandedIndex !== null && (
          <ExpandedOverlay
            experience={EXPERIENCES[expandedIndex]}
            onClose={() => setExpandedIndex(null)}
          />
        )}
      </Container>
    </Section>
  );
};

export default ExperienceSection;
